import { useMemo } from 'react';

import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Card, DataGrid, useColDefs } from '~components';
import { queryParameters } from '~constants';
import TriggerSourceType from '~features/triggers/enum/triggerSourceType';
import Trigger, { triggerGridCols } from '~features/triggers/interfaces/trigger';
import TriggersService from '~features/triggers/services/triggersService';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';

interface ScheduledActionsTriggersCardProps {
	scheduledActionId: string;
	organisationId: string;
}

const triggersService = getService(TriggersService);

const ScheduledActionsTriggersCard = ({
	scheduledActionId,
	organisationId,
}: ScheduledActionsTriggersCardProps) => {
	const { t } = useTranslation();
	const persistenceKey = 'scheduledActionTriggers';

	const {
		data: triggersData,
		error: triggersError,
		isLoading: triggersIsLoading,
	} = useSWR(
		triggersService.getTriggersCacheKey(
			1,
			50,
			TriggerSourceType.ScheduleTrigger,
			scheduledActionId,
			undefined,
			undefined,
			organisationId
		),
		([_path, pageNumber, pageSize, _sourceType, sourceId, _targetType, _targetId, orgId]) =>
			triggersService.getTriggers({
				pageNumber: pageNumber,
				pageSize: pageSize,
				sourceType: TriggerSourceType.ScheduleTrigger,
				sourceId: sourceId,
				deviceOrganisationId: orgId ?? undefined,
			}),
		{
			use: [cancellationMiddleware],
		}
	);

	const isInitialLoading = triggersIsLoading && triggersData == undefined;
	const hasError = triggersError != undefined;

	const cardTitle = (triggersData: Trigger[]) => {
		const count = triggersData?.length ?? 0;
		return t('trigger.labels.count', { ns: 'entities', count: count });
	};

	const colDefs = useColDefs<Trigger>();
	const columns = useMemo((): GridColDef<Trigger>[] => {
	return [
			colDefs.id(undefined, undefined, false),
			colDefs.gridColDefFor(triggerGridCols.deviceOrGroup, (_value: Trigger) => {
				return [[queryParameters.OverwrittenClientId, organisationId.toString()]];
			}),
			colDefs.gridColDefFor(triggerGridCols.triggerAction),
		];
	}, [colDefs, organisationId]);

	return (
		<Card title={cardTitle(triggersData?.results ?? [])} sx={{ height: '100%' }}>
			<DataGrid
				persistenceKey={persistenceKey}
				disableRowSelectionOnClick
				error={hasError}
				loading={isInitialLoading}
				columns={columns}
				rows={triggersData?.results ?? []}
				hideFooterPagination
				hideFooter
				noDataTitle={t('trigger.status.noTriggersFoundTitle', { ns: 'entities' })}
				noDataDescription={t('trigger.status.noTriggersFoundDescription', {
					ns: 'entities',
				})}
				errorTitle={t('trigger.status.triggersErrorTitle', { ns: 'entities' })}
				errorDescription={t('trigger.status.triggersErrorDescription', { ns: 'entities' })}
				paginationMode='client'
			/>
		</Card>
	);
};

export default ScheduledActionsTriggersCard;
